import { uniqueId } from "lodash-es";
import { useMemo } from "react";
import { Amount } from "types/amount";
import { OrderType } from "utils/orderTypeUtils";
import { ActivityStore } from "../store/activity/ActivityStore";
import { BoughtOrSoldFields, TRANSACTION_STATUSES, TransactionDetail } from "../types/history";
import { Hash } from "viem";
import { DUMMY_WALLET } from "../config/consts";

export const useTradeHistoryGroupedData = () => {
  const history = ActivityStore.useState(s => s.history);
  /*
    Groups the data returned from getHistory.ts by Month
    This is so we can show the Month/Year above the trades which were made in that Month/year.
   */
  const groupedData = useMemo(() => {
    if (history.error != null) return null;
    if (history.data === undefined) {
      return getMockDataActivitySkeleton(); // populate skeletons in table if history not returned yet
    }
    const sortedData = [...Object.values(history.data.results)].sort(
      (a, b) => b.timestamp.getTime() - a.timestamp.getTime()
    );

    return sortedData.reduce(
      (groups: Record<string, Array<TransactionDetail>>, item: TransactionDetail) => {
        const month = item.timestamp.getMonth();
        const year = item.timestamp.getFullYear();
        const key = `${year}-${month}`;
        if (!groups[key]) {
          groups[key] = [];
        }
        groups[key].push(item);
        return groups;
      },
      {}
    );
  }, [history]);

  return groupedData;
};

// Helpers

// Mock data for activity table to render skeletons
const getMockTransactionDetail =
  () =>
  (month: number): TransactionDetail => {
    const mockTokenAddress = `0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE` as Hash;
    return {
      chain_id: 1,
      status: TRANSACTION_STATUSES.SETTLED,
      type: OrderType.OneToMany,
      sender: DUMMY_WALLET,
      receiver: DUMMY_WALLET,
      sellTokens: { [mockTokenAddress]: {} } as Record<Hash, BoughtOrSoldFields>,
      buyTokens: { [mockTokenAddress]: {} } as Record<Hash, BoughtOrSoldFields>,
      timestamp: new Date(`2000-${month}-1`),
      volumeUsd: new Amount("0"),
      gasFeeUsd: 0.123,
      txHash: uniqueId(),
    };
  };

export const getMockDataActivitySkeleton = (): Record<string, Array<TransactionDetail>> => {
  const getMockTransactionByMonth = getMockTransactionDetail();
  return {
    "2000-3": Array(6)
      .fill(undefined)
      .map(() => getMockTransactionByMonth(4)),
    "2000-2": Array(4)
      .fill(undefined)
      .map(() => getMockTransactionByMonth(3)),
    "2000-1": Array(8)
      .fill(undefined)
      .map(() => getMockTransactionByMonth(2)),
    "2000-0": Array(4)
      .fill(undefined)
      .map(() => getMockTransactionByMonth(1)),
  };
};
